import { Injectable } from '@angular/core';
import { getApp, getApps } from 'firebase/app';
import type { FirebaseApp } from 'firebase/app';
import type { Firestore } from 'firebase/firestore';
import type { Functions } from 'firebase/functions';
import type { FirebaseStorage } from 'firebase/storage';
import { environment } from '../../environments/environment';

/**
 * Enhanced Firebase service that uses existing providers when available
 * or lazy loads when needed for optimization
 */
@Injectable({
  providedIn: 'root'
})
export class FirebaseLoaderService {
  private firebaseApp: FirebaseApp | null = null;
  private appPromise: Promise<FirebaseApp> | null = null;

  constructor() {}

  /**
   * Get Firebase App instance (lazy loaded)
   */
  async getApp(): Promise<FirebaseApp> {
    if (this.firebaseApp) {
      return this.firebaseApp;
    }

    if (this.appPromise) {
      return this.appPromise;
    }

    this.appPromise = this.initializeApp();
    return this.appPromise;
  }

  /**
   * Get Firestore instance (uses provided instance if available)
   */
  async getFirestore(): Promise<Firestore> {
    const app = await this.getApp();
    const { getFirestore } = await import('firebase/firestore');
    return getFirestore(app);
  }

  /**
   * Get Functions instance (uses provided instance if available)
   */
  async getFunctions(): Promise<Functions> {
    const app = await this.getApp();
    const { getFunctions } = await import('firebase/functions');
    return getFunctions(app);
  }

  /**
   * Get Storage instance (lazy loaded)
   */
  async getStorage(): Promise<FirebaseStorage> {
    const app = await this.getApp();
    const { getStorage } = await import('firebase/storage');
    return getStorage(app);
  }

  /**
   * Initialize Firebase App
   */
  private async initializeApp(): Promise<FirebaseApp> {
    if (this.firebaseApp) {
      return this.firebaseApp;
    }

    try {
      if (getApps().length > 0) {
        this.firebaseApp = getApp();
        return this.firebaseApp;
      }
      const { initializeApp } = await import('firebase/app');
      this.firebaseApp = initializeApp(environment.firebaseConfig);
      return this.firebaseApp;
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      throw error;
    }
  }

  /**
   * Check if Firebase is available
   */
  isFirebaseAvailable(): boolean {
    return !!environment.firebaseConfig;
  }

  /**
   * Preload Firebase modules for better UX
   * Call this when you know Firebase will be needed soon
   */
  async preloadFirebase(): Promise<void> {
    if (!this.isFirebaseAvailable()) {
      return;
    }

    try {
      // Preload all Firebase modules in parallel
      await Promise.all([
        import('firebase/app'),
        import('firebase/firestore'),
        import('firebase/functions'),
        import('firebase/storage')
      ]);
      
      // console.log('Firebase modules preloaded successfully');
    } catch (error) {
      console.warn('Failed to preload Firebase modules:', error);
    }
  }
}

